import React, { useEffect, useRef, useState } from 'react';
import DailyIframe from '@daily-co/daily-js';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import type { Call } from '../types/call';

interface VideoCallProps {
  call: Call;
  userId: string;
  onCallEnded?: () => void;
}

const VideoCall: React.FC<VideoCallProps> = ({ call, userId, onCallEnded }) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const callInstance = useRef<any>(null);
  const [isJoining, setIsJoining] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!videoRef.current) return;

    const startCall = async () => {
      try {
        callInstance.current = DailyIframe.createFrame(videoRef.current as HTMLElement, {
          iframeStyle: {
            width: '100%',
            height: '100%',
            border: '0',
            borderRadius: '12px',
          },
          showLeaveButton: true,
          showFullscreenButton: true,
        });

        callInstance.current.on('joined-meeting', async () => {
          setIsJoining(false);
          await updateDoc(doc(db, 'calls', call.id), {
            [`participants.${userId}.joined`]: true,
            [`participants.${userId}.joinedAt`]: new Date(),
          });
        });

        callInstance.current.on('left-meeting', async () => {
          await updateDoc(doc(db, 'calls', call.id), {
            [`participants.${userId}.joined`]: false,
            [`participants.${userId}.leftAt`]: new Date(),
          });
          if (onCallEnded) onCallEnded();
        });

        await callInstance.current.join({ url: call.roomUrl });
      } catch (err) {
        console.error('Error starting call:', err);
        setError('Failed to start video call');
        setIsJoining(false);
      }
    };

    startCall();

    return () => {
      if (callInstance.current) {
        callInstance.current.destroy();
      }
    };
  }, [call, userId, onCallEnded]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  if (isJoining) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return <div ref={videoRef} className="w-full h-full" />;
};

export default VideoCall;